//-- Copyright (c) 2021 TMT Insights Group, LLC
import {
  DateTimeDisplay,
  JobTypeLabel,
  JobRequirements,
  TableJobStage,
  JobsOverflowMenu,
  UserDisplay,
  Notes,
  ClickCopy
} from '@tmt-insights/oms_fe_core/columns';
import { StatusColors } from '@tmt-insights/oms_fe_core/constants';
import { AllTableColumnsConfig } from '@/__configs/config_types';
import { JobDetail, TitleEntity } from '@/redux/slices/slice_types';
import { findandFormatUTCDateInString } from '@tmt-insights/oms_fe_core/date';
import TitleHierarchy from '../../components/TitleHierarchy';

const columns: AllTableColumnsConfig<TitleEntity & JobDetail> = {
  hid: {
    label: 'Job ID',
    sortField: 'id',
    render: (cell: JobDetail['hid']) => (
      <ClickCopy copyText={cell} noBackground disableTextClick>
        <strong style={{ color: 'var(--primary-main)' }}>{cell}</strong>
      </ClickCopy>
    )
  },
  job_type: {
    label: 'Job Type',
    render: (cell: JobDetail['job_type']) => <JobTypeLabel type={cell} />
  },
  title: {
    label: 'Title',
    clickCopy: true,
    minWidth: 250,
    render: (cell: TitleEntity, row: JobDetail) => <TitleHierarchy title={cell} versionName={row.version?.name} />
  },
  version__program_id: {
    label: 'Program ID'
  },
  version__piece_id: {
    label: 'Piece ID'
  },
  version__piece_number: {
    label: 'Piece Number'
  },
  output_title__external_id: {
    label: 'Output Program ID'
  },
  output_version__external_id: {
    label: 'Output Version ID',
    tooltip: {
      title: 'Displays Core Version ID or Piece ID',
      placement: 'top'
    }
  },
  output_edit_type: {
    label: 'Output Master Type',
    sortField: 'validated_workflow_inputs__edit_type'
  },
  order__managed_by: {
    label: 'Managed By',
    disableSort: true,
    minWidth: 100,
    render: (cell: JobDetail['order']['managed_by'], row: JobDetail) => (cell ? <UserDisplay user={cell} emptyText="-" /> : row.managed_by || '-')
  },
  customer: {
    label: 'Customer',
    clickCopy: true,
    minWidth: 180
  },
  profile: {
    label: 'Profile',
    clickCopy: true,
    sortField: 'job_profile',
    minWidth: 200,
    render: (cell: JobDetail['profile']) => findandFormatUTCDateInString(cell?.name)
  },
  due_date: {
    label: 'Due Date',
    clickCopy: true,
    minWidth: 100,
    render: (cell: JobDetail['due_date']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  created_at: {
    label: 'Created',
    clickCopy: true,
    minWidth: 100,
    render: (cell: JobDetail['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  completed_at: {
    label: 'Completed',
    minWidth: 100,
    render: (cell: JobDetail['completed_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  stage: {
    label: 'Status',
    render: (cell: JobDetail['stage'], row: JobDetail) => (
      <TableJobStage status={row.status === 'cancelled' ? row.status : cell || row.status} colorOverride={StatusColors[row.status]} />
    )
  },
  assets: {
    label: 'Requirements',
    render: (cell: JobDetail['assets'], row: JobDetail) => (
      <div style={{ opacity: row.status === 'cancelled' ? '0.5' : '1', marginLeft: -15 }}>
        <JobRequirements sources={cell} />
      </div>
    ),
    tooltip: {
      title: 'Displays sources received out of the total expected',
      placement: 'top'
    }
  },
  notes: {
    label: 'Notes',
    render: (_cell: any, row: JobDetail) => (
      <div style={{ width: 40, textAlign: 'center' }}>
        <Notes data={row} type="jobs" />
      </div>
    ),
    disableSort: true
  },

  overflow: {
    label: '',
    isOverflow: true,
    render: (_cell: any, row: JobDetail) => <JobsOverflowMenu row={row} />
  }
};

export default columns;
